import React, { useState, useEffect } from 'react';
import { Mail, Trash2, CheckCircle, XCircle, Eye, Reply, Calendar, User, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import AdminLayout from '../../components/admin/AdminLayout';

const MessagesManagement = () => {
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]); // Tüm mesajları tutmak için
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread, read, replied

  useEffect(() => {
    fetchAllMessages(); // Tüm mesajları çek
    fetchMessages(); // Filtrelenmiş mesajları çek
  }, [filter]);

  const fetchAllMessages = async () => {
    try {
      const response = await fetch('http://localhost/api/contact-messages.php', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      const messagesArray = Array.isArray(data) ? data : [];
      
      // Verileri normalize et - is_read ve is_replied değerlerini integer'a çevir
      const normalizedMessages = messagesArray.map(msg => ({
        ...msg,
        is_read: msg.is_read === true || msg.is_read === 1 || msg.is_read === '1' ? 1 : 0,
        is_replied: msg.is_replied === true || msg.is_replied === 1 || msg.is_replied === '1' ? 1 : 0,
      }));
      
      setAllMessages(normalizedMessages);
    } catch (error) {
      console.error('Error fetching all messages:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      let url = 'http://localhost/api/contact-messages.php';
      
      if (filter === 'unread') {
        url += '?is_read=false';
      } else if (filter === 'read') {
        url += '?is_read=true';
      } else if (filter === 'replied') {
        url += '?is_replied=true';
      }
      
      const response = await fetch(url, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Mesajlar yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setMessages(Array.isArray(data) ? data : []);
      
      // Filtrelenmiş mesajlar değiştiğinde tüm mesajları da güncelle
      fetchAllMessages();
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Mesajlar yüklenirken hata oluştu');
    }
  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
    
    // Mesajı okundu olarak işaretle
    if (!message.is_read) {
      markAsRead(message.id);
    }
  };

  const markAsRead = async (id) => {
    try {
      const response = await fetch(`http://localhost/api/contact-messages.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_read: true }),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Mesaj işaretlenirken hata oluştu');
        return;
      }

      // State'i anında güncelle
      setAllMessages(prev => prev.map(msg => 
        msg.id === id ? { ...msg, is_read: true } : msg
      ));
      setMessages(prev => prev.map(msg => 
        msg.id === id ? { ...msg, is_read: true } : msg
      ));
      
      if (selectedMessage && selectedMessage.id === id) {
        setSelectedMessage({ ...selectedMessage, is_read: true });
      }
      
      // Arka planda verileri yeniden çek
      fetchMessages();
      fetchAllMessages();
    } catch (error) {
      console.error('Error marking as read:', error);
      toast.error('Mesaj işaretlenirken hata oluştu');
    }
  };

  const markAsUnread = async (id) => {
    try {
      const response = await fetch(`http://localhost/api/contact-messages.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_read: false }),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Mesaj işaretlenirken hata oluştu');
        return;
      }

      // State'i anında güncelle
      setAllMessages(prev => prev.map(msg => 
        msg.id === id ? { ...msg, is_read: false } : msg
      ));
      setMessages(prev => prev.map(msg => 
        msg.id === id ? { ...msg, is_read: false } : msg
      ));
      
      if (selectedMessage && selectedMessage.id === id) {
        setSelectedMessage({ ...selectedMessage, is_read: false });
      }
      
      // Arka planda verileri yeniden çek
      fetchMessages();
      fetchAllMessages();
    } catch (error) {
      console.error('Error marking as unread:', error);
      toast.error('Mesaj işaretlenirken hata oluştu');
    }
  };

  const markAsReplied = async () => {
    if (!selectedMessage) {
      return;
    }

    const newRepliedStatus = !selectedMessage.is_replied;

    try {
      const response = await fetch(`http://localhost/api/contact-messages.php?id=${selectedMessage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          is_replied: newRepliedStatus,
        }),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Mesaj işaretlenirken hata oluştu');
        return;
      }

      toast.success(newRepliedStatus ? 'Mesaj cevaplandı olarak işaretlendi' : 'Mesaj cevaplanmadı olarak işaretlendi');
      
      // State'i anında güncelle
      setAllMessages(prev => prev.map(msg => 
        msg.id === selectedMessage.id ? { ...msg, is_replied: newRepliedStatus } : msg
      ));
      setMessages(prev => prev.map(msg => 
        msg.id === selectedMessage.id ? { ...msg, is_replied: newRepliedStatus } : msg
      ));
      setSelectedMessage({ ...selectedMessage, is_replied: newRepliedStatus });
      
      // Arka planda verileri yeniden çek
      fetchMessages();
      fetchAllMessages();
    } catch (error) {
      console.error('Error marking as replied:', error);
      toast.error('Mesaj işaretlenirken hata oluştu');
    }
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;

    try {
      const response = await fetch(`http://localhost/api/contact-messages.php?id=${deleteItemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Mesaj silinirken hata oluştu');
        return;
      }

      toast.success('Mesaj silindi');
      
      // State'i anında güncelle
      setAllMessages(prev => prev.filter(msg => msg.id !== deleteItemId));
      setMessages(prev => prev.filter(msg => msg.id !== deleteItemId));
      
      setIsDeleteModalOpen(false);
      setDeleteItemId(null);
      
      if (selectedMessage && selectedMessage.id === deleteItemId) {
        setIsModalOpen(false);
        setSelectedMessage(null);
      }
      
      // Arka planda verileri yeniden çek
      fetchMessages();
      fetchAllMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Mesaj silinirken hata oluştu');
    }
  };

  // Sayıları doğru hesapla - veritabanından gelen değerler integer (0/1) olabilir
  const unreadCount = allMessages.filter(m => {
    const isRead = m.is_read;
    // Boolean false, 0, '0', null, undefined veya boş string ise okunmamış
    if (isRead === false || isRead === 0 || isRead === '0' || isRead === null || isRead === undefined || isRead === '') {
      return true;
    }
    return false;
  }).length;
  
  const readCount = allMessages.filter(m => {
    const isRead = m.is_read;
    // Boolean true, 1, veya '1' ise okundu
    if (isRead === true || isRead === 1 || isRead === '1') {
      return true;
    }
    return false;
  }).length;
  
  const repliedCount = allMessages.filter(m => {
    const isReplied = m.is_replied;
    // Boolean true, 1, veya '1' ise cevaplandı
    if (isReplied === true || isReplied === 1 || isReplied === '1') {
      return true;
    }
    return false;
  }).length;

  return (
    <AdminLayout>
      <div>
        {/* Filter Buttons */}
        <div className="mb-4 sm:mb-6 flex flex-wrap gap-2 sm:gap-3">
          <Button
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'default' : 'outline'}
            className={filter === 'all' ? 'bg-gradient-to-r from-blue-500 to-blue-600' : ''}
          >
            Tümü ({allMessages.length})
          </Button>
          <Button
            onClick={() => setFilter('unread')}
            variant={filter === 'unread' ? 'default' : 'outline'}
            className={filter === 'unread' ? 'bg-gradient-to-r from-red-500 to-red-600' : ''}
          >
            Okunmamış ({unreadCount})
          </Button>
          <Button
            onClick={() => setFilter('read')}
            variant={filter === 'read' ? 'default' : 'outline'}
            className={filter === 'read' ? 'bg-gradient-to-r from-green-500 to-green-600' : ''}
          >
            Okundu ({readCount})
          </Button>
          <Button
            onClick={() => setFilter('replied')}
            variant={filter === 'replied' ? 'default' : 'outline'}
            className={filter === 'replied' ? 'bg-gradient-to-r from-purple-500 to-purple-600' : ''}
          >
            Cevaplandı ({repliedCount})
          </Button>
        </div>

        {/* Messages List */}
        <div className="mt-6">
          {messages.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl">
              <div className="text-6xl mb-4">📭</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Henüz mesaj yok</h3>
              <p className="text-gray-600">İletişim formundan gelen mesajlar burada görünecek</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all border-l-4 ${
                    !message.is_read ? 'border-red-500' : message.is_replied ? 'border-green-500' : 'border-gray-300'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{message.name}</h3>
                        {!message.is_read ? (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                            Yeni
                          </span>
                        ) : null}
                        {message.is_replied ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1">
                            <Reply size={12} />
                            Cevaplandı
                          </span>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <Mail size={14} />
                          {message.email}
                        </div>
                        {message.phone && (
                          <div className="flex items-center gap-1">
                            <Phone size={14} />
                            {message.phone}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(message.created_at).toLocaleDateString('tr-TR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                      {message.subject && (
                        <p className="font-semibold text-gray-800 mb-2 text-sm sm:text-base break-words">Konu: {message.subject}</p>
                      )}
                      <p className="text-gray-700 line-clamp-2 mb-3 text-sm sm:text-base break-words">{message.message}</p>
                    </div>
                    <div className="flex items-center gap-2 sm:ml-4 flex-shrink-0 w-full sm:w-auto">
                      <Button
                        onClick={() => handleViewMessage(message)}
                        variant="outline"
                        size="sm"
                        className="flex-1 sm:flex-initial flex items-center justify-center gap-1 whitespace-nowrap"
                      >
                        <Eye size={16} className="flex-shrink-0" />
                        <span className="hidden sm:inline">Detay</span>
                      </Button>
                      <Button
                        onClick={() => handleDelete(message.id)}
                        variant="destructive"
                        size="sm"
                        className="flex-1 sm:flex-initial flex items-center justify-center whitespace-nowrap"
                      >
                        <Trash2 size={16} className="flex-shrink-0" />
                        <span className="hidden sm:inline">Sil</span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Message Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Mesaj Detayı</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <User size={20} className="text-gray-400" />
                  <div>
                    <p className="font-semibold text-gray-900">{selectedMessage.name}</p>
                    <p className="text-sm text-gray-600">{selectedMessage.email}</p>
                  </div>
                </div>
                {selectedMessage.phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={20} className="text-gray-400" />
                    <p className="text-gray-700">{selectedMessage.phone}</p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar size={20} className="text-gray-400" />
                  <p className="text-gray-700">
                    {new Date(selectedMessage.created_at).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                {selectedMessage.subject && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Konu:</p>
                    <p className="text-gray-900">{selectedMessage.subject}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Mesaj:</p>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={markAsReplied}
                  className={`flex-1 ${
                    selectedMessage.is_replied
                      ? 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700'
                      : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                  }`}
                >
                  <Reply size={16} className="mr-2" />
                  {selectedMessage.is_replied ? 'Cevaplanmadı Olarak İşaretle' : 'Cevaplandı Olarak İşaretle'}
                </Button>
                {selectedMessage.is_read ? (
                  <Button
                    onClick={() => markAsUnread(selectedMessage.id)}
                    variant="outline"
                    className="flex-1"
                  >
                    <XCircle size={16} className="mr-2" />
                    Okunmadı Olarak İşaretle
                  </Button>
                ) : (
                  <Button
                    onClick={() => markAsRead(selectedMessage.id)}
                    variant="outline"
                    className="flex-1"
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Okundu Olarak İşaretle
                  </Button>
                )}
                <Button
                  onClick={() => handleDelete(selectedMessage.id)}
                  variant="destructive"
                >
                  <Trash2 size={16} className="mr-2" />
                  Sil
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md w-[95vw] sm:w-full mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Silme Onayı</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">Bu mesajı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteItemId(null);
              }}
            >
              İptal
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
            >
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default MessagesManagement;
